import { useGetAllLabWorksQuery } from '@pm/graphql';
import { useTranslation } from 'react-i18next';
import { usePatientProfile } from '../../hooks/profile';
import { LabHeroCardProps } from './LabHeroCard';
import {
  ActiveLabWorkNode,
  buildLinkedResultIndex,
  heroCardPropsFor,
  isActiveLabWork,
} from './labHeroCardUtils';

export type HeroCardEntry = {
  node: ActiveLabWorkNode;
  props: Exclude<LabHeroCardProps, { kind: 'empty' }>;
};

export type UseLabHeroCardsOptions = {
  /** Skip the underlying query entirely, for callers gated behind a flag. */
  skip?: boolean;
  /**
   * Keep lab work the patient cannot act on, such as a kit the lab has
   * received but not yet reported on.
   */
  includeNonActionable?: boolean;
};

export const useLabHeroCards = ({
  skip = false,
  includeNonActionable = false,
}: UseLabHeroCardsOptions = {}): {
  heroCards: HeroCardEntry[];
  loading: boolean;
  error: Error | undefined;
} => {
  const { isUsaPatient } = usePatientProfile();
  const { t } = useTranslation('global', { keyPrefix: 'SupportLinks' });
  const countryKey = isUsaPatient ? 'USA' : 'Canada';
  const supportLink = t(`${countryKey}.ZendeskRequest`);

  const { data, loading, error } = useGetAllLabWorksQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip,
  });

  const allNodes = (data?.labWorks?.edges ?? []).map((e) => e.node);
  const linkedResultByAnchorId = buildLinkedResultIndex(allNodes);

  const isUsaOrLabReq = (node: ActiveLabWorkNode) =>
    isUsaPatient || node.__typename === 'LabRequisition';

  const heroCards = allNodes
    .filter(isActiveLabWork)
    .filter(isUsaOrLabReq)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .flatMap((node) => {
      const props = heroCardPropsFor(
        node,
        linkedResultByAnchorId.get(node.id) ?? null,
        supportLink,
        isUsaPatient,
        includeNonActionable,
      );
      return props ? [{ node, props }] : [];
    });

  return { heroCards, loading, error };
};
